/**
 * React timer wiring for `boardTransitionStage.ts`. All the actual decision logic (readiness counting, when
 * the countdown may start, that it never restarts) lives in the pure reducer and is unit-tested there
 * (`test/boardTransitionStage.test.ts`) without a DOM, matching this package's convention of not
 * unit-testing hooks directly (see e.g. `useVoice.ts` / `voice.test.ts`).
 */
import { useEffect, useReducer, useRef } from 'react';
import {
  boardTransitionStageReducer,
  initialBoardTransitionStageState,
  type BoardTransitionStage,
  type BoardTransitionStageConfig,
  type BoardTransitionStageEvent,
  type BoardTransitionStageState,
} from './boardTransitionStage.ts';

export interface UseBoardTransitionStageOptions {
  /** Everyone who must be ready before the countdown can start. */
  totalPlayers: number;
  /** How many of them are ready right now (recomputed by the caller from its own player list each render). */
  readyCount: number;
  /** Whether the LOCAL participant has pressed Ready. Only meaningful while still in `briefing`. */
  selfReady: boolean;
  /** How long the reveal stage stays on screen before advancing on its own. */
  revealDurationMs?: number;
  /** Seconds the countdown counts down from. */
  countdownSeconds?: number;
  /** Fires exactly once, the moment the countdown reaches zero. */
  onCountdownComplete?: () => void;
}

export interface UseBoardTransitionStageResult {
  stage: BoardTransitionStage;
  countdownValue: number | null;
}

export function useBoardTransitionStage(options: UseBoardTransitionStageOptions): UseBoardTransitionStageResult {
  const { totalPlayers, readyCount, selfReady, revealDurationMs = 3200, countdownSeconds = 3, onCountdownComplete } = options;
  const config: BoardTransitionStageConfig = { totalPlayers, countdownSeconds };

  const [state, dispatch] = useReducer(
    (s: BoardTransitionStageState, e: BoardTransitionStageEvent) => boardTransitionStageReducer(s, e, config),
    undefined,
    initialBoardTransitionStageState,
  );

  // reveal -> briefing, timed, nothing to click.
  useEffect(() => {
    if (state.stage !== 'reveal') return;
    const id = setTimeout(() => dispatch({ type: 'reveal_elapsed' }), revealDurationMs);
    return () => clearTimeout(id);
  }, [state.stage, revealDurationMs]);

  // briefing -> ready, the moment the local participant presses Ready.
  useEffect(() => {
    if (state.stage === 'briefing' && selfReady) dispatch({ type: 'self_ready' });
  }, [state.stage, selfReady]);

  // ready -> countdown, automatically, once everyone is ready. Re-checked whenever the stage itself changes
  // too (not just readyCount) so a player who is already ready the instant they arrive on this stage — e.g.
  // playing solo — doesn't wait for an unrelated readyCount change that may never come.
  useEffect(() => {
    dispatch({ type: 'ready_count_changed', readyCount });
  }, [readyCount, state.stage, totalPlayers]);

  // countdown ticking, one tick per second.
  useEffect(() => {
    if (state.stage !== 'countdown') return;
    const id = setInterval(() => dispatch({ type: 'countdown_tick' }), 1000);
    return () => clearInterval(id);
  }, [state.stage]);

  // Fires the completion callback exactly once — there is no second Start action to call it again.
  const firedRef = useRef(false);
  useEffect(() => {
    if (state.stage === 'starting' && !firedRef.current) {
      firedRef.current = true;
      onCountdownComplete?.();
    }
  }, [state.stage, onCountdownComplete]);

  return { stage: state.stage, countdownValue: state.countdownValue };
}
